import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { useAuth } from '../auth/auth';

// TSS vite/03 §"Cross-app server state" — the lists that turn an id into
// something a person can read. They live here and not inside a feature
// because two features already need them and neither owns them: the pulse
// asks who moved a task, the findings screen asks which repository one came
// out of, and `users` is not the `live` feature's entity in either case.
//
// EVERY ONE IS GATED BY THE PERMISSION THE BACKEND ENFORCES on that list, and
// the codes come from core/authz/roles.js. An ungated lookup is a 403 fired on
// a screen nobody asked to write to, which trips the global permission toast
// and tells the reader they did something wrong when they only opened a page.
//
// When a lookup is not readable the caller gets `#id` back instead of a name:
// less, but never wrong, and never a blank cell that reads as missing data.

const CATALOG_STALE_MS = 10 * 60_000;
const CATALOG_PAGE = 100;   // the backend's hard cap on `limit`

function catalog(path) {
    return ({ signal }) => api
        .get(path, { params: { limit: CATALOG_PAGE, offset: 0 }, signal })
        .then(response => response.data?.items ?? []);
}

export function useUsers() {
    const { can } = useAuth();
    return useQuery({
        queryKey: ['lookups', 'users'],
        queryFn: catalog('admin/users'),
        enabled: can('users:read'),
        staleTime: CATALOG_STALE_MS,
    });
}

// `tokens:admin`, not `tokens:read`: minting a credential sits behind :admin in
// core/authz/roles.js and the LIST route asks for the same one, because the
// list already tells you who has an agent and when it last ran.
export function useTokens() {
    const { can } = useAuth();
    return useQuery({
        queryKey: ['lookups', 'tokens'],
        queryFn: catalog('admin/tokens'),
        enabled: can('tokens:admin'),
        staleTime: CATALOG_STALE_MS,
    });
}

export function useProjects() {
    const { can } = useAuth();
    return useQuery({
        queryKey: ['lookups', 'projects'],
        queryFn: catalog('admin/projects'),
        enabled: can('projects:read'),
        staleTime: CATALOG_STALE_MS,
    });
}

export function useTiers() {
    const { can } = useAuth();
    return useQuery({
        queryKey: ['lookups', 'tiers'],
        queryFn: catalog('admin/tiers'),
        enabled: can('tiers:read'),
        staleTime: CATALOG_STALE_MS,
    });
}

export function useRepositories() {
    const { can } = useAuth();
    return useQuery({
        queryKey: ['lookups', 'repositories'],
        queryFn: catalog('admin/repositories'),
        enabled: can('repositories:read'),
        staleTime: CATALOG_STALE_MS,
    });
}

// The resolvers, split by what they need to ask for. One `useNames()` that
// resolved everything would fire four catalog requests on every screen that
// wanted one name — the findings list needs a repository and would have
// pulled the user and token lists with it, both of them behind permissions it
// has no reason to hold.

export function useAuthorNames() {
    const users = useUsers();
    const tokens = useTokens();

    const byUser = useMemo(
        () => new Map((users.data ?? []).map(item => [item.id, item])),
        [users.data],
    );
    const byToken = useMemo(
        () => new Map((tokens.data ?? []).map(item => [item.id, item])),
        [tokens.data],
    );

    const userName = useCallback((id) => (
        id == null ? null : (byUser.get(id)?.name ?? `Usuario #${id}`)
    ), [byUser]);

    const tokenName = useCallback((id) => (
        id == null ? null : (byToken.get(id)?.name ?? `Token #${id}`)
    ), [byToken]);

    // The token's kind is the role the backend actually applied to that call —
    // authorize() reads req.user.role from the token's `kind` — so it is the
    // honest label for an agent: `agent_slave` claimed it, not the person whose
    // account the token hangs from.
    const tokenKind = useCallback((id) => (
        id == null ? null : (byToken.get(id)?.kind ?? null)
    ), [byToken]);

    return { userName, tokenName, tokenKind };
}

export function useRepositoryName() {
    const repositories = useRepositories();

    const byRepository = useMemo(
        () => new Map((repositories.data ?? []).map(item => [item.id, item])),
        [repositories.data],
    );

    return useCallback((id) => (
        id == null ? null : (byRepository.get(id)?.name ?? `Repositorio #${id}`)
    ), [byRepository]);
}

// THE TIER'S IDENTIFIER IS IN ITS NAME, NOT IN ITS ID, and the two do not
// agree: row 19 is «Tier 15 · Canal de eventos en vivo», row 12 is «Tier 2» and
// row 9 is «Tier 10». The numbers people say out loud are the ones in the name;
// the primary key is an accident of insertion order. Showing the id as "the
// tier" would send somebody to the wrong tier file, confidently.
//
// So the chip says what the name says. A tier whose name does not start with
// «Tier N» — there are three, all superseded — keeps its whole name, because
// inventing a number for it would be worse than a long chip.
const TIER_NUMBER = /^\s*(Tier\s+\d+)/i;

export function useTierLabel() {
    const tiers = useTiers();

    const byId = useMemo(
        () => new Map((tiers.data ?? []).map(item => [item.id, item])),
        [tiers.data],
    );

    return useCallback((id) => {
        if (id == null) return null;
        const tier = byId.get(id);
        if (!tier) return { short: `Tier #${id}`, full: null };

        const match = TIER_NUMBER.exec(tier.name ?? '');
        return { short: match ? match[1] : tier.name, full: tier.name };
    }, [byId]);
}

// El proyecto, por su nombre. Mismo respaldo que los demás: `#id` antes que una
// celda vacía, y nada si la cuenta no puede leer proyectos.
export function useProjectName() {
    const projects = useProjects();

    const byId = useMemo(
        () => new Map((projects.data ?? []).map(item => [item.id, item])),
        [projects.data],
    );

    return useCallback((id) => (
        id == null ? null : (byId.get(id)?.name ?? `Proyecto #${id}`)
    ), [byId]);
}

// El repositorio ENTERO, no solo su nombre.
//
// `GET /admin/tasks/{id}` trae el repositorio en `where.repository` con tres
// campos —id, name, main_branch— y sin `kind`: el ensamblador carga la fila
// completa y el paquete se queda con esos tres (T16.2). Así que la insignia de
// tipo, que es lo que hace legible un repositorio de un vistazo, sale de acá.
//
// Sin petición extra: es el mismo catálogo que ya alimenta `useRepositoryName`.
export function useRepositoryInfo() {
    const repositories = useRepositories();

    const byId = useMemo(
        () => new Map((repositories.data ?? []).map(item => [item.id, item])),
        [repositories.data],
    );

    return useCallback((id) => (id == null ? null : (byId.get(id) ?? null)), [byId]);
}
